import type { MarketplaceFulfillmentStatus } from "@nexus/contracts";
import { Tooltip, TooltipContent, TooltipTrigger, cn } from "@nexus/ui";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { getFulfillmentStatusLabel } from "../../lib/fulfillment-status-label";
import { getOrderStatusVisual } from "../../lib/order-status-visual";
import type { StatusRollup } from "../../lib/orders-list-model";

const PIPELINE_ORDER: readonly MarketplaceFulfillmentStatus[] = [
  "SCRIPT",
  "SENT_TO_PRODUCTION",
  "SUBMITTED_FOR_FEEDBACK",
  "SENT_FOR_REVISION",
  "SUBMITTED_FOR_FINAL_APPROVAL",
  "ORDER_DELIVERED",
  "COMPLETED_PRE_ADGE",
];

interface OrderStatusRollupProps {
  rollup: StatusRollup;
}

/** Read-only parent aggregate; status editing lives on scripts or the selection bar. */
export function OrderStatusRollup({ rollup }: OrderStatusRollupProps) {
  const { t } = useTranslation();
  const mentionCount = rollup.counts[rollup.mentionStatus] ?? 0;
  const label = rollup.uniformStatus
    ? t("marketplace.orders.list.rollup.uniform", {
        complete: rollup.total,
        total: rollup.total,
        status: t(
          `marketplace.orders.list.rollup.status.${rollup.uniformStatus}`,
        ),
      })
    : t("marketplace.orders.list.rollup.mixed", {
        detail: t(
          `marketplace.orders.list.rollup.mixedDetail.${rollup.mentionStatus}`,
          {
            count: mentionCount,
          },
        ),
      });
  const statusSignature = PIPELINE_ORDER.map(
    (status) => rollup.counts[status] ?? 0,
  ).join(":");
  const previousSignature = useRef(statusSignature);
  const shouldAnimate = previousSignature.current !== statusSignature;

  useEffect(() => {
    previousSignature.current = statusSignature;
  }, [statusSignature]);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="flex min-w-0 flex-col gap-1" aria-label={label}>
          <span
            key={label}
            className={cn(
              "truncate text-xs font-medium text-foreground",
              shouldAnimate &&
                "animate-in fade-in-0 slide-in-from-left-1 duration-150 ease-[cubic-bezier(0.25,1,0.5,1)] motion-reduce:animate-none",
            )}
          >
            {label}
          </span>
          <span
            key={statusSignature}
            aria-hidden
            className={cn(
              "flex h-1 w-full max-w-32 origin-left overflow-hidden rounded-full bg-muted",
              shouldAnimate &&
                "animate-in fade-in-0 slide-in-from-left-1 duration-300 ease-[cubic-bezier(0.25,1,0.5,1)] motion-reduce:animate-none",
            )}
          >
            {PIPELINE_ORDER.map((status) => {
              const count = rollup.counts[status] ?? 0;
              if (count === 0) return null;
              return (
                <span
                  key={status}
                  style={{ flexGrow: count }}
                  className={cn(
                    "min-w-1",
                    getOrderStatusVisual(status).segmentClass,
                  )}
                />
              );
            })}
          </span>
        </span>
      </TooltipTrigger>
      <TooltipContent>
        <ul className="flex flex-col gap-1">
          {PIPELINE_ORDER.map((status) => {
            const count = rollup.counts[status] ?? 0;
            if (count === 0) return null;
            return (
              <li key={status} className="flex items-center gap-2 tabular-nums">
                <span
                  aria-hidden
                  className={cn(
                    "size-1.5 rounded-full",
                    getOrderStatusVisual(status).dotClass,
                  )}
                />
                {count} × {getFulfillmentStatusLabel(t, status, "client")}
              </li>
            );
          })}
        </ul>
      </TooltipContent>
    </Tooltip>
  );
}
